
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';

interface PnolDispatchAdvance {
  advanceRequestId: string;
  companyId: string;
  taxId: string | null;
  businessName: string | null;
  netAmount: string;
}

interface PnolDispatchDetail {
  id: string;
  s3Key: string;
  advanceCount: number;
  totalAmount: string;
  triggerSource: 'cron' | 'manual';
  processedAt: string | null;
  createdAt: string;
  advances: PnolDispatchAdvance[];
}

const formatCLP = (n: number) =>
  n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

interface Props {
  dispatchId: string;
}

export default function PnolDispatchDetail({ dispatchId }: Props) {
  const [data, setData] = useState<PnolDispatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = getAdminEndpointUrl('pnolDispatches', 'detail', { id: dispatchId });
      const detail = await adminHttpClient.get<PnolDispatchDetail>(url);
      setData(detail);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [dispatchId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = getAdminEndpointUrl('pnolDispatches', 'downloadUrl', { id: dispatchId });
      const result = await adminHttpClient.get<{ url: string; expiresInSeconds: number; s3Key: string }>(url);
      window.open(result.url, '_blank', 'noopener');
    } catch (err) {
      alert(`No se pudo generar la URL de descarga: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleMarkProcessed = async () => {
    setMarking(true);
    try {
      const url = getAdminEndpointUrl('pnolDispatches', 'markProcessed', { id: dispatchId });
      await adminHttpClient.post(url);
      setConfirmOpen(false);
      await fetchDetail();
    } catch (err) {
      alert(`No se pudo marcar como enviado: ${(err as Error).message}`);
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">Cargando lote…</div>;
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
        <p className="font-medium">No se pudo cargar el lote.</p>
        <p className="mt-1">{error ?? 'Lote no encontrado.'}</p>
        <Link to="/admin/pnol-dispatches" className="mt-3 inline-flex items-center gap-1 text-xs underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  const isProcessed = data.processedAt !== null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/pnol-dispatches" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <span className="material-symbols-outlined text-[14px]">arrow_back</span>
          Volver al listado
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Generado" value={formatDateTime(data.createdAt)} subtitle={data.id} mono />
        <Card label="Origen" value={data.triggerSource === 'cron' ? 'Cron programado' : 'Trigger manual'} />
        <Card label="Adelantos" value={String(data.advanceCount)} />
        <Card label="Monto total" value={formatCLP(Number(data.totalAmount))} />
      </div>

      <div className={`rounded-xl border p-4 ${isProcessed ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30' : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30'}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className={`material-symbols-outlined text-[24px] ${isProcessed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {isProcessed ? 'check_circle' : 'pending'}
            </span>
            <div>
              <p className={`text-sm font-semibold ${isProcessed ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}>
                {isProcessed ? 'Lote enviado a BCI' : 'Lote pendiente de envío'}
              </p>
              <p className={`text-xs ${isProcessed ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {isProcessed
                  ? `Marcado como enviado el ${formatDateTime(data.processedAt!)}`
                  : 'Descarga el archivo, súbelo al portal de BCI, y luego marca el lote como enviado.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleDownload} disabled={downloading}>
              <span className="material-symbols-outlined mr-1 text-[18px]">download</span>
              {downloading ? 'Generando URL…' : 'Descargar archivo'}
            </Button>
            {!isProcessed && (
              <Button onClick={() => setConfirmOpen(true)} disabled={marking}>
                <span className="material-symbols-outlined mr-1 text-[18px]">send</span>
                Marcar como enviado
              </Button>
            )}
          </div>
        </div>
        <p className="mt-3 text-[11px] font-mono text-muted-foreground break-all">{data.s3Key}</p>
      </div>

      <div className="rounded-xl border bg-card overflow-x-auto">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold">Adelantos incluidos en el lote</h3>
          <p className="text-xs text-muted-foreground">Una fila por línea del archivo PNOL.</p>
        </div>
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-3 px-4">Comercio</th>
              <th className="py-3 px-4">RUT</th>
              <th className="py-3 px-4">ID adelanto</th>
              <th className="py-3 px-4 text-right">Monto neto</th>
            </tr>
          </thead>
          <tbody>
            {data.advances.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Este lote no tiene adelantos vinculados.</td></tr>
            )}
            {data.advances.map((adv) => (
              <tr key={adv.advanceRequestId} className="border-t border-border">
                <td className="py-3 px-4 font-medium">{adv.businessName ?? '—'}</td>
                <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{adv.taxId ?? '—'}</td>
                <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{adv.advanceRequestId}</td>
                <td className="py-3 px-4 text-right font-medium tabular-nums">{formatCLP(Number(adv.netAmount))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Confirmar envío del lote</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              ¿Confirmas que ya enviaste el archivo PNOL a BCI? Esta acción registra la fecha de envío y no se puede deshacer.
            </p>
            <div className="mt-3 rounded-md bg-muted px-3 py-2 text-xs">
              <p>Lote: <span className="font-mono">{data.id}</span></p>
              <p>{data.advanceCount} adelanto(s) · {formatCLP(Number(data.totalAmount))}</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={marking}>Cancelar</Button>
              <Button onClick={handleMarkProcessed} disabled={marking}>
                {marking ? 'Marcando…' : 'Confirmar envío'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, subtitle, mono }: { label: string; value: string; subtitle?: string; mono?: boolean }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className="mt-1 text-base font-semibold text-foreground">{value}</p>
      {subtitle && <p className={`mt-0.5 text-xs text-muted-foreground ${mono ? 'font-mono' : ''}`}>{subtitle}</p>}
    </div>
  );
}
